// utils/rsaEncryption.js
class RSAEncryption {
  constructor() {
    this.publicKey = null;
  }

  // Import public key from base64 string (PEM format without headers)
  async importPublicKey(publicKeyBase64) {
    try {
      // Convert base64 to ArrayBuffer
      const binaryString = window.atob(publicKeyBase64);
      const publicKeyBuffer = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        publicKeyBuffer[i] = binaryString.charCodeAt(i);
      }
      
      // Import the public key
      this.publicKey = await crypto.subtle.importKey(
        'spki',
        publicKeyBuffer.buffer,
        {
          name: 'RSA-OAEP',
          hash: 'SHA-256'
        },
        false,
        ['encrypt']
      );
      
      return true;
    } catch (error) {
      console.error('Failed to import public key:', error);
      throw new Error('Failed to import public key');
    }
  }

  // Encrypt data with public key
  async encrypt(data) {
    try {
      if (!this.publicKey) {
        throw new Error('Public key not loaded. Call importPublicKey first.');
      }

      // Convert data to JSON string then to ArrayBuffer
      const jsonString = JSON.stringify(data);
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(jsonString);

      // Check if data is too large for RSA encryption (max ~190 bytes for 2048-bit key with OAEP)
      if (dataBuffer.byteLength > 190) {
        throw new Error('Data too large for RSA encryption. Consider using hybrid encryption.');
      }

      // Encrypt data
      const encryptedBuffer = await crypto.subtle.encrypt(
        {
          name: 'RSA-OAEP'
        },
        this.publicKey,
        dataBuffer
      );

      // Convert to base64 string
      return this.arrayBufferToBase64(encryptedBuffer);
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error(`Encryption failed: ${error.message}`);
    }
  }

  // Helper method to convert ArrayBuffer to base64
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  // Check if public key is loaded
  isReady() {
    return this.publicKey !== null;
  }

  // Clear the public key (for security)
  clear() {
    this.publicKey = null;
  }
}

// Create singleton instance
const rsaEncryption = new RSAEncryption();

export default rsaEncryption;